import type { TimelineWeight } from '@/domain/fishGroups';
import { formatDateShortLocalized } from '@/utils/date';
import { formatWeightByLocale, gramsToKilograms } from '@/utils/localeFormatters';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** "0.47 kg" / "0,47 kg", as the design and the rest of the portal show a fish weight. */
export function formatBoardWeight(grams: number, locale: string): string {
    return formatWeightByLocale(gramsToKilograms(grams), 'kg', locale, 2);
}

export interface WeightLabel {
    text: string;
    /** Says where the number comes from, or why there is none. */
    title: string;
}

/**
 * The row's weight: the number, "Not licensed", or the "– kg" placeholder when nothing was
 * estimated. An ended group reads "0.41 kg at merge".
 */
export function rowWeightLabel(
    weight: TimelineWeight,
    endedEvent: string | null,
    locale: string,
    t: Translate
): WeightLabel {
    if (weight.status === 'notLicensed') {
        return {
            text: t('board.row.weightNotLicensed'),
            title: t('board.row.weightNotLicensedTitle'),
        };
    }
    const available = weight.status === 'available' && weight.livingWeight !== null && weight.day;
    const value = available
        ? formatBoardWeight(weight.livingWeight ?? 0, locale)
        : t('board.row.weightPlaceholder');
    return {
        text: endedEvent ? t('board.row.weightAt', { weight: value, event: endedEvent }) : value,
        title: available
            ? t('board.row.weightAsOf', { date: formatDateShortLocalized(weight.day, locale) })
            : t('board.row.weightNoData'),
    };
}

/** What a stay's bar adds to its tooltip about weight; null when there is nothing to say. */
export function stayWeightText(
    weight: TimelineWeight | null,
    locale: string,
    t: Translate
): string | null {
    if (!weight) return null;
    if (weight.status === 'notLicensed') return t('board.row.stayNotLicensed');
    if (weight.status !== 'available' || weight.livingWeight === null || !weight.day) return null;
    return t('board.row.stayWeight', {
        weight: formatBoardWeight(weight.livingWeight, locale),
        date: formatDateShortLocalized(weight.day, locale),
    });
}
